import { useEffect, useMemo, useState } from 'react'
import { IPVPNavigation } from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { assessmentApi } from '@/utils/api'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

type QuestionOption = {
  optionText: string
  optionValue?: number
  orderIndex?: number
}

type Question = {
  id: string
  questionText: string
  sectionNumber: number
  category?: string
  questionType?: string
  orderIndex?: number
  isForMale?: boolean
  isForFemale?: boolean
  options?: QuestionOption[]
}

const defaultOption = { optionText: '', optionValue: 0, orderIndex: 1 }
const questionTypes = [
  { label: 'Multiple Choice', value: 'multiple_choice' },
  { label: 'Likert Scale', value: 'likert_scale' },
  { label: 'Yes / No', value: 'yes_no' },
  { label: 'Text', value: 'text' },
]

export default function AdminQuestionsPage() {
  const { user, logout } = useAuth()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)

  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editing, setEditing] = useState<Question | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null)
  const [filterSection, setFilterSection] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [availableSections, setAvailableSections] = useState<number[]>([])
  const [availableCategories, setAvailableCategories] = useState<string[]>([])
  // const [needsMetadata, setNeedsMetadata] = useState(false)
  const [newCategory, setNewCategory] = useState('')

  const [form, setForm] = useState({
    questionText: '',
    sectionNumber: 1,
    category: '',
    questionType: 'likert_scale',
    orderIndex: 1,
    isForMale: false,
    isForFemale: false,
    options: [defaultOption],
  })

  const fetchQuestions = async () => {
    setLoading(true)
    setError(null)
    try {
      const params: { section?: number; category?: string } = {}
      if (filterSection) params.section = Number(filterSection)
      if (filterCategory) params.category = filterCategory

      const res = await assessmentApi.listQuestions(params)
      const list = (res as { data?: Question[] }).data || (res as unknown as Question[])
      const arr = Array.isArray(list) ? list : []
      setQuestions(arr)
      // derive available sections/categories for filters
      const sectionsSet = new Set<number>()
      const categorySet = new Set<string>()
      arr.forEach((q) => {
        if (q.sectionNumber) sectionsSet.add(q.sectionNumber)
        if (q.category) categorySet.add(q.category)
      })
      const sectionsArr = Array.from(sectionsSet).sort((a, b) => a - b)
      const categoryArr = Array.from(categorySet).sort()
      setAvailableSections(sectionsArr)
      setAvailableCategories(categoryArr)
      // setNeedsMetadata(sectionsArr.length === 0 || categoryArr.length === 0)
    } catch (err) {
      setError((err as Error)?.message || 'Unable to load questions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchQuestions()
  }, [filterSection, filterCategory])

  const resetForm = () => {
    setEditing(null)
    setForm({
      questionText: '',
      sectionNumber: 1,
    category: '',
      questionType: 'likert_scale',
      orderIndex: 1,
      isForMale: false,
      isForFemale: false,
      options: [defaultOption],
    })
  }

  const openForEdit = (q: Question) => {
    setEditing(q)
    setForm({
      questionText: q.questionText,
      sectionNumber: q.sectionNumber,
      category: q.category || '',
      questionType: q.questionType || 'likert_scale',
      orderIndex: q.orderIndex || 1,
      isForMale: !!q.isForMale,
      isForFemale: !!q.isForFemale,
      options: q.options && q.options.length 
        ? q.options.map((opt, idx) => ({
            optionText: opt.optionText,
            optionValue: opt.optionValue ?? 0,
            orderIndex: opt.orderIndex ?? idx + 1
          }))
        : [defaultOption],
    })
    setSheetOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      const payload = {
        questionText: form.questionText,
        sectionNumber: Number(form.sectionNumber),
        category: form.category || undefined,
        questionType: form.questionType,
        orderIndex: Number(form.orderIndex),
        isForMale: form.isForMale,
        isForFemale: form.isForFemale,
        options:
          form.questionType === 'text'
            ? []
            : form.options
                .filter((o) => o.optionText.trim())
                .map((o, idx) => ({
                  optionText: o.optionText,
                  optionValue: o.optionValue ?? 0,
                  orderIndex: o.orderIndex ?? idx + 1,
                })),
      }
      if (editing?.id) {
        await assessmentApi.updateQuestion(editing.id, payload)
      } else {
        await assessmentApi.createQuestion(payload)
      }
      setSheetOpen(false)
      resetForm()
      await fetchQuestions()
    } catch (err) {
      setError((err as Error)?.message || 'Unable to save question')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    setDeleteLoading(id)
    setError(null)
    try {
      await assessmentApi.deleteQuestion(id)
      await fetchQuestions()
    } catch (err) {
      setError((err as Error)?.message || 'Unable to delete question')
    } finally {
      setDeleteLoading(null)
    }
  }

  const filteredQuestions = useMemo(() => questions, [questions])

  const handleSignOut = () => {
    logout()
    window.location.href = '/login'
  }

  return (
    <div className='w-full min-h-screen bg-[#F8F9FA] px-4 py-6 md:px-6 lg:px-8 space-y-6'>
      <div className='bg-white rounded-lg shadow-sm p-4'>
        <IPVPNavigation
          title=' Questions'
          infoText='Manage assessment questions and options.'
          showLocation={false}
          userName={userName}
          userInitials={userInitials}
          onSignOut={handleSignOut}
          only
        />
      </div>

      <div className='bg-white rounded-xl border border-gray-100 p-4 shadow-sm space-y-4'>
        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3'>
          <div className='flex flex-wrap gap-2 text-sm'>
            <select
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
              className='rounded-lg border border-gray-200 px-3 py-2 min-w-[140px] text-sm'
            >
              <option value=''>All Sections</option>
              {availableSections.map((s) => (
                <option key={s} value={s}>{`Section ${s}`}</option>
              ))}
            </select>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className='rounded-lg border border-gray-200 px-3 py-2 min-w-[160px] text-sm'
            >
              <option value=''>All Categories</option>
              {availableCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <Button
              variant='ghost'
              className='text-sm'
              onClick={() => {
                setFilterCategory('')
                setFilterSection('')
              }}
            >
              Clear Filters
            </Button>
          </div>
          <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetForm() }}>
            <SheetTrigger asChild>
              <Button className='bg-gray-900 hover:bg-gray-800 text-white text-sm'>Add Question</Button>
            </SheetTrigger>
            <SheetContent side='right' className='w-full sm:w-[500px] md:w-[640px]'>
              <SheetHeader>
                <SheetTitle>{editing ? 'Edit Question' : 'Add Question'}</SheetTitle>
              </SheetHeader>
              <form onSubmit={handleSave} className='mt-4 space-y-3 overflow-y-auto max-h-[80vh] pr-1'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Question Text</label>
                  <textarea
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                    required
                    value={form.questionText}
                    onChange={(e) => setForm({ ...form, questionText: e.target.value })}
                  />
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Section #</label>
                    <input
                      type='number'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      required
                      value={form.sectionNumber}
                      onChange={(e) => setForm({ ...form, sectionNumber: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Category</label>
                    <div className='flex gap-2'>
                      <select
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                      >
                        <option value=''>Select category</option>
                        {availableCategories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                        {newCategory && !availableCategories.includes(newCategory) && (
                          <option value={newCategory}>{newCategory}</option>
                        )}
                      </select>
                      <input
                        className='w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm'
                        placeholder='Add new'
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        onBlur={() => {
                          if (newCategory && !availableCategories.includes(newCategory)) {
                            setAvailableCategories((prev) => [...prev, newCategory])
                            setForm((prev) => ({ ...prev, category: newCategory }))
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Category</label>
                    <input
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Order Index</label>
                    <input
                      type='number'
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      value={form.orderIndex}
                      onChange={(e) => setForm({ ...form, orderIndex: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Question Type</label>
                    <select
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                      value={form.questionType}
                      onChange={(e) => setForm({ ...form, questionType: e.target.value })}
                    >
                      {questionTypes.map((qt) => (
                        <option key={qt.value} value={qt.value}>
                          {qt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='flex items-center gap-3 pt-6'>
                    <label className='inline-flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='checkbox'
                        checked={form.isForMale}
                        onChange={(e) => setForm({ ...form, isForMale: e.target.checked })}
                      />
                      Male
                    </label>
                    <label className='inline-flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='checkbox'
                        checked={form.isForFemale}
                        onChange={(e) => setForm({ ...form, isForFemale: e.target.checked })}
                      />
                      Female
                    </label>
                  </div>
                </div>

                {form.questionType !== 'text' && (
                  <div className='space-y-2'>
                    <div className='flex items-center justify-between'>
                      <h4 className='text-sm font-semibold text-gray-900'>Options</h4>
                      <div className='flex items-center gap-2'>
                        <Button
                          type='button'
                          variant='ghost'
                          className='text-orange-600 text-xs'
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              options: [...prev.options, { ...defaultOption, orderIndex: prev.options.length + 1 }],
                            }))
                          }
                        >
                          Add Option
                        </Button>
                        <Button
                          type='button'
                          variant='ghost'
                          className='text-orange-600 text-xs'
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              options: [
                                { optionText: 'Never', optionValue: 1, orderIndex: 1 },
                                { optionText: 'Rarely', optionValue: 2, orderIndex: 2 },
                                { optionText: 'Sometimes', optionValue: 3, orderIndex: 3 },
                                { optionText: 'Often', optionValue: 4, orderIndex: 4 },
                                { optionText: 'Always', optionValue: 5, orderIndex: 5 },
                              ],
                            }))
                          }
                        >
                          Add Likert Defaults
                        </Button>
                      </div>
                    </div>
                    {form.options.map((opt, idx) => (
                      <div key={idx} className='grid grid-cols-12 gap-2 items-center'>
                        <input
                          className='col-span-7 rounded-lg border border-gray-300 px-3 py-2 text-sm'
                          placeholder='Option text'
                          value={opt.optionText}
                          onChange={(e) =>
                            setForm((prev) => {
                              const next = [...prev.options]
                              next[idx] = { 
                                optionText: e.target.value,
                                optionValue: next[idx]?.optionValue ?? 0,
                                orderIndex: next[idx]?.orderIndex ?? idx + 1
                              }
                              return { ...prev, options: next }
                            })
                          }
                        />
                        <input
                          className='col-span-3 rounded-lg border border-gray-300 px-3 py-2 text-sm'
                          type='number'
                          placeholder='Value'
                          value={opt.optionValue ?? 0}
                          onChange={(e) =>
                            setForm((prev) => {
                              const next = [...prev.options]
                              next[idx] = { 
                                optionText: next[idx]?.optionText ?? '',
                                optionValue: Number(e.target.value),
                                orderIndex: next[idx]?.orderIndex ?? idx + 1
                              }
                              return { ...prev, options: next }
                            })
                          }
                        />
                        <input
                          className='col-span-2 rounded-lg border border-gray-300 px-3 py-2 text-sm'
                          type='number'
                          placeholder='Order'
                          value={opt.orderIndex ?? idx + 1}
                          onChange={(e) =>
                            setForm((prev) => {
                              const next = [...prev.options]
                              next[idx] = { 
                                optionText: next[idx]?.optionText ?? '',
                                optionValue: next[idx]?.optionValue ?? 0,
                                orderIndex: Number(e.target.value)
                              }
                              return { ...prev, options: next }
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                {(error && sheetOpen) && (
                  <p className='text-sm text-red-600'>{error}</p>
                )}

                <Button type='submit' disabled={saving} className='w-full bg-gray-900 hover:bg-gray-800'>
                  {saving ? 'Saving...' : editing ? 'Update Question' : 'Create Question'}
                </Button>
              </form>
            </SheetContent>
          </Sheet>
        </div>

        <div className='overflow-x-auto'>
          <div className='grid grid-cols-12 gap-2 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 text-start'>
            <span className='col-span-3'>Question</span>
            <span className='col-span-2'>Section</span>
            <span className='col-span-2'>Category</span>
            <span className='col-span-2'>Type</span>
            <span className='col-span-1 text-center'>Order</span>
            <span className='col-span-2 text-right'>Actions</span>
          </div>
          {loading && <p className='text-sm text-gray-600 p-4'>Loading questions...</p>}
          {!loading && filteredQuestions.length === 0 && (
            <p className='text-sm text-gray-600 p-4'>No questions found.</p>
          )}
          {!loading &&
            filteredQuestions.map((q) => (
              <div
                key={q.id}
                className='grid grid-cols-12 gap-2 px-4 py-3 text-sm text-gray-800 border-t border-gray-100 items-start text-start'
              >
                <span className='col-span-3'>
                  <span className='font-semibold text-gray-900'>{q.questionText}</span>
                  {q.options && q.options.length > 0 && (
                    <div className='text-xs text-gray-600 mt-1 space-y-0.5'>
                      {q.options.slice(0, 3).map((opt, idx) => (
                        <div key={idx}>
                          {opt.optionText} <span className='text-gray-400'>(value {opt.optionValue ?? 0})</span>
                        </div>
                      ))}
                      {q.options.length > 3 && <div>+{q.options.length - 3} more</div>}
                    </div>
                  )}
                </span>
                <span className='col-span-2'>{`Section ${q.sectionNumber}`}</span>
                <span className='col-span-2'>{q.category || '—'}</span>
                <span className='col-span-2 capitalize'>{q.questionType || '—'}</span>
                <span className='col-span-1 text-center'>{q.orderIndex ?? '—'}</span>
                <span className='col-span-2 flex justify-end gap-2 text-xs'>
                  <button
                    className='px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200'
                    onClick={() => openForEdit(q)}
                  >
                    Edit
                  </button>
                  <button
                    className='px-3 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50'
                    onClick={() => handleDelete(q.id)}
                    disabled={deleteLoading === q.id}
                  >
                    {deleteLoading === q.id ? 'Deleting...' : 'Delete'}
                  </button>
                </span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
